// src/utils/NewsNotificationContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { AuthContext } from './AuthContext';
import { getNewsItems, getUserLastNewsVisit } from './firestoreUtils';

const NewsNotificationContext = createContext();

export const useNewsNotifications = () => {
  const ctx = useContext(NewsNotificationContext);
  if (!ctx) throw new Error('useNewsNotifications must be used within NewsNotificationProvider');
  return ctx;
};

export const NewsNotificationProvider = ({ children }) => {
  const { user } = useContext(AuthContext);
  const [hasNewNews, setHasNewNews] = useState(false);

  const checkForNewNews = useCallback(async () => {
    if (!user) { setHasNewNews(false); return; }
    try {
      const lastVisit = await getUserLastNewsVisit(user.uid);
      if (!lastVisit) { setHasNewNews(false); return; }
      const items = await getNewsItems();
      const lastVisitDate = lastVisit.toDate ? lastVisit.toDate() : new Date(lastVisit);
      const hasNew = items.some(item => {
        const created = item.createdAt ? new Date(item.createdAt) : null;
        return created && created > lastVisitDate;
      });
      setHasNewNews(hasNew);
    } catch { setHasNewNews(false); }
  }, [user]);

  useEffect(() => { checkForNewNews(); }, [checkForNewNews]);

  useEffect(() => {
    if (!user) return;
    const interval = setInterval(checkForNewNews, 30000);
    return () => clearInterval(interval);
  }, [user, checkForNewNews]);

  const clearNotification = () => setHasNewNews(false);

  return (
    <NewsNotificationContext.Provider value={{ hasNewNews, clearNotification, checkForNewNews }}>
      {children}
    </NewsNotificationContext.Provider>
  );
};
